import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    WMButton,
    WMButtonVariant,
    WMConditionalEllipsis,
    WMIconDelete,
    WMSelect,
    WMSelectOption,
    WMSelectRef,
    WMSelectValue,
    WMTextField
} from '@walkme/wm-ui';
import clsx from 'clsx';
import classes from './Rule.module.scss';
import { useDispatch } from 'react-redux';
import debounce from '@material-ui/core/utils/debounce';
import { useTranslation } from 'react-i18next';

import { FieldErrors, useFormContext } from 'react-hook-form';

import { USER_IDENTIFIER_PROPERTY } from '../../../../../state/reducers/audiences';
import { components } from 'react-select';
import { ReactComponent as UserIcon } from '../../../../../../assets/icons/user-id.svg';
import { recipientsSearchMinSymbols, VALUES_FETCH_SIZE } from '../../../consts';
import {
    AggregateRecipientsBodyPropertyDataTypeEnum,
    AggregateRecipientsBodyPropertyInput,
    AudienceRuleGraphql,
    GetRecipientsQueryVariables,
    GetRecipientsIncludingInactiveQuery,
    GetRecipientsIncludingInactiveDocument,
    AudienceRuleOperatorEnum,
    AudienceRuleDataTypeEnum,
    RecipientsFilter,
    UserRecipientAttributesFragmentDoc,
    GetSelectedRecipientsAttributesQuery,
    GetSelectedRecipientsAttributesQueryVariables,
    UserRecipientAttributesFragment,
    GetSelectedRecipientsAttributesDocument
} from '../../../../../../generated/apollo-hooks';
import { ApolloQueryResult, useApolloClient } from '@apollo/client';
import { useIsMountedRef } from '../../../../../helpers/useIsMounted';
import logger from '../../../../../services/logger.service';
import { enqueueSnackbar } from '../../../../../state/actions/ui';
import { useActiveRecipientsAggregations } from '../../../hooks/recipients';
import { createPropertyOptions, isActiveUser } from '../../utils';
import VirtualMenuList from './VirtualMenuList';
import _omit from 'lodash/omit';
import _groupBy from 'lodash/groupBy';
import isString from 'lodash/isString';
import { getFormatNumberString } from '../../../../../helpers/getFormatNumberString';
import { AudienceFormFields } from '../../CreateAudienceDialog';
import { CreateAudienceDialogContext } from '../../CreateAudienceDialogContext';
import { useSelector } from '../../../../../infrastructure/store.interface';

const keyPrefix = 'components.audiences.createAudienceDialog.audienceRulesEditor.rule';

const PreFixTag = ({ index, isFirstRule, hasNotifications }: { index: number; isFirstRule: boolean; hasNotifications: boolean }) => {
    const { t } = useTranslation('', { keyPrefix });
    return (
        <div
            className={clsx({
                [classes.and]: !isFirstRule,
                [classes.who]: isFirstRule,
                [classes.notificationDisabled]: hasNotifications
            })}
            data-testid={`createAudienceDialog-rule-prefix-tag-${index}`}
        >
            {isFirstRule ? t('who') : t('and')}
        </div>
    );
};

function alwaysShow() {
    return true;
}
const PROPERTY_VALUE_MAX_WIDTH = 156;

const debounceLoadingValuesFunction = debounce(async (func: $TSFixMe) => {
    await func();
}, 500);

const selectedRuleMatchesUserIntegration = (
    userIntegrationValue: string | boolean,
    selectedValues: { name: unknown }[],
    operator?: AudienceRuleOperatorEnum
): boolean => {
    if (operator === AudienceRuleOperatorEnum.Contains) {
        return String(userIntegrationValue).includes(String(selectedValues[0].name));
    }
    return selectedValues.some(it => String(it.name) === String(userIntegrationValue));
};

const operatorsWithSelectUi: string[] = [AudienceRuleOperatorEnum.In, AudienceRuleOperatorEnum.NotIn];
const noop = () => null;

function Rule({
    index,
    errors,
    isFirstRule,
    propertyOptions,
    property,
    propertyValues,
    isSingleRule,
    hasNotifications,
    onChangeProperty,
    onChangeValue,
    onChangeOperator,
    onDeleteRule,
    handleOnMenuClose,
    selectPropertyAutofocus
}: {
    index: number;
    errors?: Partial<Record<keyof AudienceRuleGraphql, FieldErrors>>;
    isFirstRule: boolean;
    propertyValues: WMSelectOption[] | undefined;
    property: ReturnType<typeof createPropertyOptions>[number];
    propertyOptions: WMSelectOption[];
    isSingleRule: boolean;
    hasNotifications: boolean;
    onChangeProperty: (property: WMSelectOption) => void;
    onChangeValue: (values: WMSelectOption[]) => void;
    onChangeOperator: (value: WMSelectOption) => void;
    onDeleteRule: () => void;
    handleOnMenuClose: () => void;
    selectPropertyAutofocus: boolean;
}) {
    const { t } = useTranslation('', { keyPrefix });
    const dispatch = useDispatch();
    const selectPropertyRef = useRef<WMSelectRef>(null);
    const inputValueRef = useRef<HTMLInputElement>(null);
    const [propertyLocal, setPropertyLocal] = useState<WMSelectOption | null>(
        property.value ? { ...property, label: <WMConditionalEllipsis value={property.label} /> } : null
    );

    const [valuesLocal, setValueLocal] = useState<WMSelectOption[] | undefined>(
        propertyValues?.map((it: WMSelectOption & { amount?: number }) => ({
            ...it,
            label: <WMConditionalEllipsis value={`${it.name}${it.amount ? ` (${getFormatNumberString(it.amount)})` : ''}`} />,
            multiValueLabel: <WMConditionalEllipsis value={String(it.value!)} />
        }))
    );

    const valueForInput = operatorsWithSelectUi.includes(property.operator) ? '' : (propertyValues?.[0]?.value as string) ?? '';
    const [inputValueLocal, setInputValueLocal] = useState<string>(valueForInput);

    const operatorOptions: WMSelectOption[] = useMemo(
        () => [
            {
                label: t('is'),
                value: AudienceRuleOperatorEnum.In
            },
            {
                label: t('like'),
                value: AudienceRuleOperatorEnum.Contains
            },
            {
                label: t('isNot'),
                value: AudienceRuleOperatorEnum.NotIn
            }
        ],
        [t]
    );
    const [operatorLocal, setOperatorLocal] = useState(operatorOptions.find(it => it.value === property.operator)!);

    const [propertyValuesOptions, setPropertyValuesOptions] = useState<WMSelectOption[]>([]);

    const [showUnfilteredOpts, setShowUnfilteredOpts] = useState(false);
    const [selectPropertyValuesLoading, setSelectPropertyValuesLoading] = useState(false);

    const [selectValuesSearch, setSelectValuesSearch] = useState('');
    const [onNoSearchTerm, setOnNoSearchTerm] = useState(true);

    const initialDataLoadTriggered = useRef(false);

    const apolloClient = useApolloClient();

    const isMountedRef = useIsMountedRef();

    const { getValues } = useFormContext<AudienceFormFields>();

    const initialAggregationsVariables: {
        rules: AudienceRuleGraphql[];
        properties: AggregateRecipientsBodyPropertyInput[];
        mustIncludeValues?: string[];
    } = { rules: [], properties: [] };

    const [aggregationsVariable, setAggregationsVariable] = useState(initialAggregationsVariables);

    const { aggregationsProperties, loading: aggregationLoading, error: aggregationError } = useActiveRecipientsAggregations({
        variables: aggregationsVariable,
        skip: !aggregationsVariable?.properties.length,
        onError: () => {
            dispatch(
                enqueueSnackbar({
                    message: t('cantLoadValue', { property: property.label }),
                    options: { variant: 'error' }
                })
            );
        }
    });

    const isLoadingValues = useMemo(() => selectPropertyValuesLoading || aggregationLoading, [selectPropertyValuesLoading, aggregationLoading]);
    const [hideValuesOnLoading, setHideValuesOnLoading] = useState(true);

    const [allValuesLoaded, setAllValuesLoaded] = useState(false);

    useEffect(() => {
        const { integrationId, property, dataType } = aggregationsVariable.properties?.[0] || {};

        if (propertyLocal?.value !== property || propertyLocal?.integrationId !== integrationId) {
            return;
        }

        if (aggregationsProperties && !aggregationLoading && !aggregationError) {
            try {
                const propertyAggregationResult = aggregationsProperties.find(
                    aggregation => aggregation.integrationId === integrationId && aggregation.property === property
                );

                if (!propertyAggregationResult) {
                    return;
                }

                const { values, hasMoreValues } = propertyAggregationResult;

                if (onNoSearchTerm && hasMoreValues !== undefined) setAllValuesLoaded(!hasMoreValues);

                if (values?.length === undefined) {
                    throw "didn't got a list of values";
                }
                const valuesHash = aggregationsVariable.mustIncludeValues?.reduce((acc, it) => {
                    acc[`${it}`] = 1;
                    return acc;
                }, {});

                const sortedByAmount = [...values].sort((a, b) => {
                    const v1 = a.name;
                    const v2 = b.name;

                    if (valuesHash?.[v1] && !valuesHash?.[v2]) {
                        return -1;
                    }
                    if (!valuesHash?.[v1] && valuesHash?.[v2]) {
                        return 1;
                    }
                    return b.amount - a.amount;
                });

                setPropertyValuesOptions(
                    sortedByAmount.map(value => {
                        const name = String(
                            dataType === AggregateRecipientsBodyPropertyDataTypeEnum.Boolean && !isNaN(Number(value.name))
                                ? Boolean(Number(value.name))
                                : value.name
                        );

                        return {
                            value: name,
                            label: (
                                <WMConditionalEllipsis
                                    value={`${name} (${getFormatNumberString(value.amount)})`}
                                    maxWidth={PROPERTY_VALUE_MAX_WIDTH}
                                />
                            ),
                            amount: value.amount,
                            multiValueLabel: <WMConditionalEllipsis value={name} />
                        };
                    })
                );
            } catch (error) {
                dispatch(
                    enqueueSnackbar({
                        message: t('cantLoadValue', { property }),
                        options: { variant: 'error' }
                    })
                );
                logger.error(`error in loading values of property ${property}`, error);
            }
        }
    }, [propertyLocal, onNoSearchTerm, aggregationsVariable, aggregationsProperties, aggregationLoading, aggregationError, dispatch, t]);

    const fetchMoreValuesOnSearch = useMemo(() => {
        return !allValuesLoaded;
    }, [allValuesLoaded]);

    const updatePropertyValueOptions = ({
        onSearch = false,
        _selectValuesSearch = selectValuesSearch
    }: {
        onSearch?: boolean;
        _selectValuesSearch?: string;
    }) => {
        if (!propertyLocal || !isMountedRef.current) {
            return;
        }

        if (onSearch && fetchMoreValuesOnSearch) {
            setSelectPropertyValuesLoading(true);

            debounceLoadingValuesFunction(() => {
                loadPropertyValueOptions(onSearch, _selectValuesSearch);
            });
        } else {
            loadPropertyValueOptions(onSearch);
        }
    };

    const [recipientRulesMap, setRecipientRulesMap] = useState<{
        [recipientId: string]: { [integrationId: string]: { [propertyName: string]: string | boolean } };
    } | null>(null);

    const { editMode } = useContext(CreateAudienceDialogContext);

    const attributesFetchedForSelectedRecipients = useRef(false);
    const systemId = useSelector(store => store.common.currentSystem);
    const attributesForSelectedRecipientsFetched = attributesFetchedForSelectedRecipients.current;

    const getLoadOptionsPromises = useCallback(
        (search: string) => {
            const rules = getValues('rules').filter(it => it.property === USER_IDENTIFIER_PROPERTY && it.values.length);
            const getOptionsQuery = apolloClient.query<GetRecipientsIncludingInactiveQuery, GetRecipientsQueryVariables>({
                query: GetRecipientsIncludingInactiveDocument,
                variables: {
                    query: {
                        search,
                        page: 0,
                        limit: VALUES_FETCH_SIZE,
                        recipientsFilter: RecipientsFilter.All
                    }
                }
            });
            if (editMode && propertyLocal?.value === USER_IDENTIFIER_PROPERTY && !attributesForSelectedRecipientsFetched && rules) {
                return [
                    getOptionsQuery,
                    apolloClient
                        .query<GetSelectedRecipientsAttributesQuery, GetSelectedRecipientsAttributesQueryVariables>({
                            query: GetSelectedRecipientsAttributesDocument,
                            variables: {
                                selectedRecipiensRuleArray: rules.map(rule => ({
                                    ..._omit(rule, 'integrationType', 'role')
                                })),
                                systemId
                            }
                        })
                        .then(res => {
                            attributesFetchedForSelectedRecipients.current = true;
                            return res;
                        })
                ];
            }
            return [getOptionsQuery];
        },
        [apolloClient, attributesForSelectedRecipientsFetched, editMode, getValues, propertyLocal?.value, systemId]
    );

    const loadPropertyValueOptions = useCallback(
        async (onSearch: any, _selectValuesSearch = selectValuesSearch) => {
            setSelectPropertyValuesLoading(true);

            const { integrationId, property, dataType } = propertyLocal?.raw ?? {};

            setHideValuesOnLoading(false);
            const searchStringPassedBorderValueDown = _selectValuesSearch.length < recipientsSearchMinSymbols && !onNoSearchTerm;

            if (
                !onSearch ||
                !initialDataLoadTriggered.current ||
                (fetchMoreValuesOnSearch && (_selectValuesSearch.length >= recipientsSearchMinSymbols || searchStringPassedBorderValueDown))
            ) {
                setShowUnfilteredOpts(searchStringPassedBorderValueDown);
                initialDataLoadTriggered.current = true;

                const search = searchStringPassedBorderValueDown ? '' : _selectValuesSearch;
                setOnNoSearchTerm(!search || search === '');

                if (propertyLocal?.value === USER_IDENTIFIER_PROPERTY) {
                    try {
                        const allRecipientsRulesMap: typeof recipientRulesMap = {};
                        allRecipientsRulesMap;
                        const optionsLoadPromises = getLoadOptionsPromises(search);
                        const [{ data }] = await Promise.all(
                            optionsLoadPromises as Promise<ApolloQueryResult<GetRecipientsIncludingInactiveQuery>>[]
                        );
                        const topOptsMap =
                            valuesLocal?.map(({ value }) => {
                                const { properties, lastSeen } =
                                    apolloClient.readFragment<UserRecipientAttributesFragment>({
                                        id: `UserRecipientGraphql:${value}`,
                                        fragment: UserRecipientAttributesFragmentDoc
                                    }) || {};
                                const recipientRulesMap2 = properties?.reduce<Record<string, Record<string, string | boolean>>>(
                                    (acc, { property, value, integrationId }) => {
                                        acc[integrationId!] = acc[integrationId!] || {};
                                        acc[integrationId!][property!] = value;
                                        return acc;
                                    },
                                    {}
                                );
                                if (recipientRulesMap2) {
                                    allRecipientsRulesMap[`${value}`] = recipientRulesMap2;
                                }

                                const now = new Date();

                                const isActive = isActiveUser(lastSeen, now);
                                return {
                                    value,
                                    label: (
                                        <WMConditionalEllipsis
                                            value={String(value)}
                                            tooltipProps={{
                                                enterNextDelay: 250,
                                                TransitionProps: {
                                                    exit: false
                                                }
                                            }}
                                        />
                                    ),
                                    amount: 1,
                                    isActive
                                };
                            }) ?? [];

                        if (data) {
                            const { recipients, count } = data.getRecipients;
                            if (!onSearch) setAllValuesLoaded(count === recipients?.length);
                            const now = new Date();

                            const opts = data.getRecipients.recipients
                                .filter(({ id }) => {
                                    return !(id in allRecipientsRulesMap);
                                })
                                .slice(0, !search ? VALUES_FETCH_SIZE - topOptsMap.length : VALUES_FETCH_SIZE)
                                .map(recipient => {
                                    const recipientRulesMap = recipient.properties?.reduce<Record<string, Record<string, string | boolean>>>(
                                        (acc, { property, value, integrationId }) => {
                                            // @ts-expect-error ts-migrate(2538) FIXME: Type 'null' cannot be used as an index type.
                                            acc[integrationId] = acc[integrationId] || {};
                                            // @ts-expect-error ts-migrate(2538) FIXME: Type 'null' cannot be used as an index type.
                                            acc[integrationId][property] = value;
                                            return acc;
                                        },
                                        {}
                                    );
                                    if (recipientRulesMap) {
                                        allRecipientsRulesMap[recipient.id] = recipientRulesMap;
                                    }

                                    const isActive = isActiveUser(recipient.lastSeen, now);
                                    return {
                                        value: recipient.id,
                                        label: (
                                            <WMConditionalEllipsis
                                                value={recipient.label}
                                                tooltipProps={{
                                                    enterNextDelay: 250,
                                                    TransitionProps: {
                                                        exit: false
                                                    }
                                                }}
                                            />
                                        ),
                                        amount: 1,
                                        isActive
                                    };
                                });
                            if (Object.keys(allRecipientsRulesMap).length) {
                                setRecipientRulesMap(allRecipientsRulesMap);
                            }

                            setPropertyValuesOptions(topOptsMap.concat(opts));
                        }
                    } catch (error) {
                        dispatch(
                            enqueueSnackbar({
                                message: t('cantLoadRecipients'),
                                options: { variant: 'error' }
                            })
                        );
                        logger.error(`error in loading recipients`, error);
                    }
                } else {
                    try {
                        setHideValuesOnLoading(!onSearch);
                        const currentRules = getValues('rules');
                        const variables = {
                            rules: currentRules
                                .filter(rule => rule.values?.length && !(rule.property === property && rule.integrationId === integrationId))
                                .map(rule => ({
                                    ..._omit(rule, 'integrationType', 'role')
                                })),
                            properties: [
                                {
                                    integrationId: integrationId as string,
                                    property: property as string,
                                    dataType: dataType,
                                    limit: VALUES_FETCH_SIZE,
                                    ...(search && search != '' ? { search } : {})
                                }
                            ]
                        };
                        if (valuesLocal?.length) {
                            Object.assign(variables, { mustIncludeValues: valuesLocal.map(it => it.value) });
                        }

                        setAggregationsVariable(variables);
                    } catch (error) {
                        dispatch(
                            enqueueSnackbar({
                                message: t('cantLoadValue', { property }),
                                options: { variant: 'error' }
                            })
                        );
                        logger.error(`error in loading values of property ${property}`, error);
                    }
                }
            } else {
                const onSearchUnderBorder = onSearch && fetchMoreValuesOnSearch && _selectValuesSearch.length < recipientsSearchMinSymbols;
                setShowUnfilteredOpts(onSearchUnderBorder);
            }

            setSelectPropertyValuesLoading(false);
        },
        [
            selectValuesSearch,
            propertyLocal?.raw,
            propertyLocal?.value,
            onNoSearchTerm,
            fetchMoreValuesOnSearch,
            getLoadOptionsPromises,
            valuesLocal,
            getValues,
            apolloClient,
            dispatch,
            t
        ]
    );

    const handlePropertyChange = async (newValue: WMSelectValue) => {
        setShowUnfilteredOpts(false);
        setValueLocal([]);
        setAllValuesLoaded(false);
        setPropertyLocal(newValue as WMSelectOption);
        setPropertyValuesOptions([]);
        initialDataLoadTriggered.current = false;

        onChangeProperty(newValue as WMSelectOption);
        setTimeout(() => {
            selectPropertyRef.current?.focus?.();
        }, 0);
    };

    const handleOnFocusValuesSelect = async () => {
        await updatePropertyValueOptions({});
    };

    const handleOperatorChange = (newValue: WMSelectValue) => {
        if (!Array.isArray(newValue)) {
            setValueLocal([]);
            setInputValueLocal('');
            setOperatorLocal(newValue);
            setPropertyValuesOptions([]);
            onChangeOperator(newValue);
            setTimeout(() => {
                inputValueRef?.current?.focus?.();
            }, 0);
        }
    };

    const updateScheduledRef = useRef(false);

    useEffect(() => {
        if (updateScheduledRef.current) {
            onChangeValue(valuesLocal!);
            updateScheduledRef.current = false;
        }
    }, [onChangeValue, valuesLocal]);

    const handleValueChange = useCallback((newValue: WMSelectValue) => {
        setValueLocal(newValue as WMSelectOption[]);
        // React Select component will rerender menu if handleValueChange function will change, which
        // cause menu to scroll up after selecting/deselecting
        // Using ref is required to run onChange callback without triggering react-select menu rerender
        updateScheduledRef.current = true;
    }, []);

    const handleInputValueChange: React.ChangeEventHandler<HTMLInputElement> = newValue => {
        const value = newValue.target.value;
        setInputValueLocal(value);
        onChangeValue(value ? [{ value } as WMSelectOption] : []);
    };

    const handleValuesSearchInputChange = (inputValue: $TSFixMe, { action }: $TSFixMe) => {
        if (action === 'set-value') return;

        setSelectValuesSearch(inputValue);
        updatePropertyValueOptions({ onSearch: true, _selectValuesSearch: inputValue });
    };

    const isOptionSelected = (value: WMSelectOption) => value.value === propertyLocal?.value && value.integrationId === propertyLocal?.integrationId;

    const groupedOptions: $TSFixMe = useMemo(() => {
        const nonIdOpts = propertyOptions.filter(propertyOption => propertyOption.value !== USER_IDENTIFIER_PROPERTY);
        const byIntegrationName = _groupBy(nonIdOpts, 'integrationName');

        const groupeByIntegration = Object.entries(byIntegrationName)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .filter(([_, opts]) => !!opts.length)
            .map(([integrationName, opts]) => {
                return {
                    label: integrationName,
                    icon: noop,
                    options: opts
                        .sort((a, b) => (a.label! < b.label! ? -1 : 1))
                        .map(propertyOption => {
                            return {
                                ...propertyOption,
                                label: isString(propertyOption.label) ? <WMConditionalEllipsis value={propertyOption.label} /> : propertyOption.label
                            };
                        })
                };
            });
        return [
            {
                label: t('uniqueId'),
                icon: UserIcon,
                options: [propertyOptions.find(propertyOption => propertyOption.value === USER_IDENTIFIER_PROPERTY)]
            },
            ...groupeByIntegration
        ];
    }, [propertyOptions, t]);

    const CustomGroupHeading = (props: $TSFixMe) => {
        const GroupIcon = props.data.icon;

        return (
            <div className={classes.selectGroupHeading}>
                <components.GroupHeading className={classes.selectGroupHeadingTitle} {...props} />
                <GroupIcon />
            </div>
        );
    };

    const CustomPropertyOption = (props: $TSFixMe) => {
        return <components.Option className={classes.propertyOption} {...props} />;
    };

    const filterOptionsProp = useMemo(() => (showUnfilteredOpts ? { filterOption: alwaysShow } : {}), [showUnfilteredOpts]);

    const valueOptions = useMemo<WMSelectOption[]>(() => {
        const opts = hideValuesOnLoading && isLoadingValues ? [] : propertyValuesOptions;
        if (propertyLocal?.value !== USER_IDENTIFIER_PROPERTY) {
            return opts;
        }
        const otherNonIdRules = getValues('rules').filter(it => it.values.length && it.property !== USER_IDENTIFIER_PROPERTY);
        return opts.map((option, index) => {
            let matchesRules = true;
            if (otherNonIdRules?.length) {
                matchesRules = otherNonIdRules.every(({ property, values, integrationId, operator }) => {
                    const userId = option.value as string;
                    return (
                        // @ts-expect-error ts-migrate(2339) FIXME: Property 'value' does not exist on type 'never'.
                        recipientRulesMap?.[userId]?.[integrationId]?.[property] !== undefined &&
                        // @ts-expect-error ts-migrate(2339) FIXME: Property 'value' does not exist on type 'never'.
                        selectedRuleMatchesUserIntegration(recipientRulesMap[userId][integrationId][property], values, operator)
                    );
                });
            }
            return {
                ...option,
                multiValueLabel: <WMConditionalEllipsis value={option.label as string} />,
                label: (
                    <span
                        data-testid={`wmSelect-menu-createAudienceDialog-ID-rule-option-${index}`}
                        {...(option.isActive && matchesRules ? {} : { className: classes.selectPropertyOptionValue })}
                    >
                        <WMConditionalEllipsis value={option.label as string} maxWidth={PROPERTY_VALUE_MAX_WIDTH} />
                        {(!option.isActive || !matchesRules) && (
                            <>
                                <span
                                    className={
                                        option.isActive ? classes.selectPropertyOptionValueDoesntMatch : classes.selectPropertyOptionValueInactive
                                    }
                                >
                                    {option.isActive ? t('doesntMatch') : t('inactive')}
                                </span>
                            </>
                        )}
                    </span>
                )
            };
        });
    }, [getValues, hideValuesOnLoading, isLoadingValues, propertyLocal?.value, propertyValuesOptions, recipientRulesMap, t]);

    const isOnlyInOperatorRule = property.raw.dataType !== AudienceRuleDataTypeEnum.String;
    const groupedValuesTooltipProps = useMemo(
        () => ({
            title: valuesLocal!.map(item => item.value).join(', ')
        }),
        [valuesLocal]
    );
    return (
        <div className={classes.rule} data-testid={`createAudienceDialog-rule-wrapper-${index}`}>
            <PreFixTag index={index} isFirstRule={isFirstRule} hasNotifications={hasNotifications} />
            <WMSelect
                openMenuOnFocus
                className={classes.select}
                placeholder={t('selectProperty')}
                value={propertyLocal}
                options={groupedOptions}
                components={{ GroupHeading: CustomGroupHeading, Option: CustomPropertyOption }}
                onChange={handlePropertyChange}
                testKey={`createAudienceDialog-rule-property-${index}`}
                isOptionSelected={isOptionSelected}
                error={errors?.property?.message}
                autoFocus={selectPropertyAutofocus}
                allOptionsLabel={t('selectAll')}
                noOptionsMessage={() => t('noOptions')}
                loadingMessage={() => t('loading')}
            />
            <WMSelect
                openMenuOnFocus
                className={classes.operatorSelect}
                value={operatorLocal}
                options={operatorOptions}
                onChange={handleOperatorChange}
                testKey={`createAudienceDialog-rule-operator-${index}`}
                isDisabled={isOnlyInOperatorRule}
                isOptionSelected={isOptionSelected}
                error={errors?.operator?.message}
                isSearchable={false}
            />

            {operatorsWithSelectUi.includes(operatorLocal.value as string) ? (
                <WMSelect
                    openMenuOnFocus
                    ref={selectPropertyRef}
                    className={clsx(classes.select, {
                        [classes.lastRuleMargin]: isSingleRule,
                        [classes.notificationDisabled]: hasNotifications
                    })}
                    placeholder={t('selectFilter')}
                    value={valuesLocal}
                    blurInputOnSelect={false}
                    onFocus={handleOnFocusValuesSelect}
                    options={valueOptions}
                    onInputChange={handleValuesSearchInputChange}
                    {...filterOptionsProp}
                    inputValue={selectValuesSearch}
                    isLoading={isLoadingValues}
                    onChange={handleValueChange}
                    isDisabled={!propertyLocal}
                    groupValues
                    groupedValuesTooltipProps={groupedValuesTooltipProps}
                    isMulti
                    multiValueGroupLabel={t('selected')}
                    allowSelectAll={
                        (!isLoadingValues || !hideValuesOnLoading) &&
                        !!propertyValuesOptions.length &&
                        !!propertyLocal &&
                        !!valuesLocal &&
                        propertyLocal.value !== USER_IDENTIFIER_PROPERTY
                    }
                    testKey={`createAudienceDialog-rule-value-${index}`}
                    onMenuClose={handleOnMenuClose}
                    error={errors?.values?.message}
                    captureMenuScroll={false}
                    components={{ MenuList: VirtualMenuList }}
                    allOptionsLabel={t('selectAll')}
                    noOptionsMessage={() => t('noOptions')}
                    loadingMessage={() => t('loading')}
                />
            ) : (
                <WMTextField
                    className={clsx(classes.select, {
                        [classes.lastRuleMargin]: isSingleRule,
                        [classes.notificationDisabled]: hasNotifications
                    })}
                    placeholder={t('typeHere')}
                    value={inputValueLocal}
                    onChange={handleInputValueChange}
                    inputRef={inputValueRef}
                    onBlur={handleOnMenuClose}
                    error={errors?.values?.message}
                    inputProps={{
                        'data-testid': `createAudienceDialog-rule-value-input-${index}`
                    }}
                />
            )}

            {!isSingleRule && (
                <WMButton
                    className={clsx(classes.deleteButton, { [classes.notificationDisabled]: hasNotifications })}
                    variant={WMButtonVariant.Text}
                    isIconButton
                    onClick={onDeleteRule}
                    iconComponent={<WMIconDelete />}
                    data-testid={`createAudienceDialog-rule-deleteButton-${index}`}
                />
            )}
        </div>
    );
}

export default Rule;
